import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getStoredUser } from "../../utils/tokenStorage";
import Logo from "../Logo";

// Role groups for easier menu permission management
const managementRoles = [
  "managing_director",
  "head_of_department_project_and_operation",
  "head_of_department_sales_and_services",
  "head_of_department_rd",
  "head_of_human_resources",
];

const primaryNav = [
  {
    label: "Requisitions",
    icon: "requisition",
    expandable: true,
    basePath: "/dashboard/requests",
    children: [
      {
        label: "Dashboard",
        icon: "home",
        href: "/dashboard/requests/dashboard",
      },

      {
        label: "Requests",
        icon: "request",
        expandable: true,
        basePath: "/dashboard/requests",
        // Exclude paths that should not auto-open this Requests group
        excludePaths: [
          "/dashboard/requests/drafts",
          "/dashboard/requests/payments",
        ],
        children: [
          { label: "Imprest", href: "/dashboard/requests/imprest" },
          { label: "Retirement", href: "/dashboard/requests/retirement" },
          // { label: "Petty Cash", href: "/dashboard/requests/petty-cash" },
          // {
          //   label: "Purchase Requisition",
          //   href: "/dashboard/requests/purchase-requisition",
          // },
          // { label: "Claim", href: "/dashboard/requests/claim" },
          // { label: "Local Purchase Order", href: "/dashboard/requests/lpo" },
          // {
          //   label: "Payment Voucher",
          //   href: "/dashboard/requests/payment-voucher",
          // },
        ],
      },
      {
        label: "Draft",
        icon: "draft",
        href: "/dashboard/requests/drafts",
      },
      {
        label: "Approvals",
        icon: "approval",
        expandable: true,
        basePath: "/dashboard/requisitions/approvals",
        // People who can act on approvals
        allowedRoles: [
          ...managementRoles,
          "project_manager",
          "project_supervisor",
          "accountant",
        ],
        children: [
          { label: "Pending", href: "/dashboard/requisitions/approvals" },
          { label: "Done", href: "/dashboard/requisitions/approvals?view=done" },
        ],
      },
      {
        label: "Archived",
        icon: "archive",
        href: "/dashboard/requisitions/archived",
      },
      {
        label: "My Requisitions",
        icon: "my-requisitions",
        href: "/dashboard/requisitions/my-requisitions",
      },
      {
        label: "Requisitions History",
        icon: "my-requisitions",
        href: "/dashboard/requisitions/my-requisitions-history",
        allowedRoles: ["accountant", "managing_director", "project_manager", "project_supervisor", "head_of_department_project_and_operation", "head_of_department_sales_and_services", "head_of_department_rd", "head_of_human_resources"],
      },
      {
        label: "Payments",
        icon: "wallet",
        href: "/dashboard/requests/payments",
        allowedRoles: ["accountant", "managing_director"],
      },
      {
        label: "Finances",
        icon: "wallet",
        expandable: true,
        basePath: "/dashboard/finances",
        children: [
          {
            label: "Budget",
            icon: "budget",
            href: "/dashboard/finances/budget",
            // Core finance & planning roles
            allowedRoles: [
              ...managementRoles,
              "project_manager",
              "accountant",
              "business_analyst",
            ],
          },
          {
            label: "Budget Approvals",
            icon: "approval",
            href: "/dashboard/budget-approvals",
            allowedRoles: ["managing_director"],
          },
          {
            label: "Cost Centers",
            icon: "cost-center",
            href: "/dashboard/finances/cost-centers",
            allowedRoles: [
              ...managementRoles,
              "accountant",
              "business_analyst",
              "project_manager"
            ],
          },
          {
            label: "Currencies",
            icon: "currency",
            href: "/dashboard/finances/currencies",
            allowedRoles: ["managing_director", "accountant", "project_manager"],
          },
          {
            label: "Exchange Rates",
            icon: "exchange-rate",
            href: "/dashboard/finances/exchange-rates",
            allowedRoles: ["managing_director", "accountant"],
          },
        ],
      },
      {
        label: "Reports",
        icon: "chart",
        expandable: true,
        // High-level reporting for management & analysis
        allowedRoles: [
          ...managementRoles,
          "project_manager",
          "accountant",
          "business_analyst",
        ],
      },
    ],
  },
  {
    label: "Administration",
    icon: "admin",
    expandable: true,
    basePath: "/dashboard/settings",
    // Only visible to admin and managing director (but see super-user override below)
    allowedRoles: [
      "admin",
      "managing_director",
      "project_manager",
      "project_supervisor",
      "head_of_department_sales_and_services",
    ],
    children: [
      {
        label: "Clients",
        icon: "client",
        href: "/dashboard/clients",
        allowedRoles: [
          "admin",
          "managing_director",
          "project_manager",
          "project_supervisor",
          "head_of_department_sales_and_services",
        ],
      },
      {
        label: "Projects",
        icon: "folder",
        href: "/dashboard/settings/projects",
        allowedRoles: [
          "admin",
          "managing_director",
          "project_manager",
          "project_supervisor",
          "head_of_department_sales_and_services",
        ],
      },
      {
        label: "Users",
        icon: "users",
        href: "/dashboard/settings/users",
        allowedRoles: ["admin", "managing_director"],
      },
      {
        label: "Workflow Config",
        icon: "settings",
        href: "/dashboard/settings/workflow-configs",
        allowedRoles: [
          "admin",
          "managing_director",
          "project_manager",
          "project_supervisor",
        ],
      },
    ],
  },
];

const iconMap = {
  home: (
    <svg
      className="h-5 w-5"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M3 9.5 12 3l9 6.5V21a.5.5 0 0 1-.5.5H4.5A.5.5 0 0 1 4 21v-8" />
      <path d="M9 21v-6h6v6" />
    </svg>
  ),
  plus: (
    <svg
      className="h-5 w-5"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 5v14" />
      <path d="M5 12h14" />
    </svg>
  ),
  folder: (
    <svg
      className="h-5 w-5"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M3.5 6.5h6l2 2h9v9a1.5 1.5 0 0 1-1.5 1.5h-15A1.5 1.5 0 0 1 3 17.5v-9a1 1 0 0 1 1-1Z" />
    </svg>
  ),
  users: (
    <svg
      className="h-5 w-5"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M17 21v-2a4 4 0 0 0-4-4H7a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
    </svg>
  ),
  "user-group": (
    <svg
      className="h-5 w-5"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 7a4 4 0 0 1 0 8" />
    </svg>
  ),
  wallet: (
    <svg
      className="h-5 w-5"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M3 7h18v10H3Z" />
      <path d="M16 12h2" />
      <path d="M3 7l12-4v4" />
    </svg>
  ),
  megaphone: (
    <svg
      className="h-5 w-5"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M3 11V7a2 2 0 0 1 2-2h2l8-3v20l-8-3H5a2 2 0 0 1-2-2v-4" />
      <path d="M3 11h4l3 9" />
    </svg>
  ),
  wrench: (
    <svg
      className="h-5 w-5"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M15.7 4.3a4 4 0 0 0-5.66 5.66l-7.04 7.05a1.5 1.5 0 0 0 2.12 2.12l7.05-7.04a4 4 0 0 0 5.53-5.79Z" />
    </svg>
  ),
  chart: (
    <svg
      className="h-5 w-5"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M3 3v18h18" />
      <path d="M7 16l4-4 3 3 5-7" />
    </svg>
  ),
  settings: (
    <svg
      className="h-5 w-5"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V22a2 2 0 0 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H2a2 2 0 0 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33h.09a1.65 1.65 0 0 0 1-1.51V2a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51h.09a1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82v.09a1.65 1.65 0 0 0 1.51 1H22a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1Z" />
    </svg>
  ),
  admin: (
    <svg
      className="h-5 w-5"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <path d="M9 4v18" />
      <path d="M3 9h18" />
      <path d="M3 14h18" />
      <path d="M9 9h6" />
      <path d="M9 14h6" />
    </svg>
  ),
  client: (
    <svg
      className="h-5 w-5"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 11.13a4 4 0 0 1 0 7.75" />
    </svg>
  ),
  requisition: (
    <svg
      className="h-5 w-5"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2" />
      <rect x="9" y="3" width="6" height="4" rx="1" />
      <path d="M9 12h6" />
      <path d="M9 16h6" />
    </svg>
  ),
  request: (
    <svg
      className="h-5 w-5"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="9" y1="15" x2="15" y2="15" />
    </svg>
  ),
  approval: (
    <svg
      className="h-5 w-5"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
      <polyline points="22 4 12 14.01 9 11.01" />
    </svg>
  ),
  archive: (
    <svg
      className="h-5 w-5"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="3" y="4" width="18" height="4" rx="1" />
      <path d="M5 8h14v10a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V8" />
      <line x1="10" y1="12" x2="14" y2="12" />
    </svg>
  ),
  "my-requisitions": (
    <svg
      className="h-5 w-5"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
      <path d="M12 11v6" />
      <path d="M9 14h6" />
    </svg>
  ),
  draft: (
    <svg
      className="h-5 w-5"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
      <polyline points="10 9 9 9 8 9" />
    </svg>
  ),
  budget: (
    <svg
      className="h-5 w-5"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="4" y="6" width="16" height="12" rx="2" />
      <path d="M8 10h8M8 14h8" />
      <path d="M12 6V4" />
    </svg>
  ),
  "cost-center": (
    <svg
      className="h-5 w-5"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <path d="M3 9h18M9 3v18" />
      <circle cx="6" cy="6" r="1" />
      <circle cx="18" cy="6" r="1" />
      <circle cx="6" cy="18" r="1" />
      <circle cx="18" cy="18" r="1" />
    </svg>
  ),
  currency: (
    <svg
      className="h-5 w-5"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="10" />
      <path d="M12 6v12M15 9H9.5a2.5 2.5 0 0 0 0 5h5a2.5 2.5 0 0 1 0 5H6" />
    </svg>
  ),
  "exchange-rate": (
    <svg
      className="h-5 w-5"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
    </svg>
  ),
};

// Helper: filter navigation items based on user roles.
// - Admin and managing_director are treated as super users and see all items
// - If a parent has allowedRoles, all of its children inherit those roles
//   unless they define their own allowedRoles.
const filterNavByRole = (items, userRoles) => {
  const roles = Array.isArray(userRoles) ? userRoles : [];
  const isSuperUser =
    roles.includes("admin") || roles.includes("managing_director");

  const walk = (nodes, inheritedAllowedRoles) =>
    (nodes || [])
      .map((node) => {
        const explicitAllowed =
          Array.isArray(node.allowedRoles) && node.allowedRoles.length > 0
            ? node.allowedRoles
            : null;

        // Effective roles for this node: its own, or whatever it inherited
        const effectiveAllowedRoles = explicitAllowed || inheritedAllowedRoles;

        if (
          !isSuperUser &&
          Array.isArray(effectiveAllowedRoles) &&
          effectiveAllowedRoles.length > 0
        ) {
          const hasRole = effectiveAllowedRoles.some((r) => roles.includes(r));
          if (!hasRole) {
            return null;
          }
        }

        const children = node.children
          ? walk(node.children, effectiveAllowedRoles)
          : undefined;

        // If this node is just a container and ends up with no children, hide it
        if (!node.href && (!children || children.length === 0)) {
          return null;
        }

        return { ...node, children };
      })
      .filter(Boolean);

  return walk(items, undefined);
};

// Compute which sections should be open based on the current path.
// Keeps the rules simple:
// - A node "matches" if the current path matches its href or starts with its basePath
//   (respecting excludePaths if defined)
// - Any expandable node that matches (directly or via any descendant) is opened.
const computeOpenSections = (items, pathname) => {
  const state = {};

  const visit = (node) => {
    const { href, basePath, excludePaths, expandable, children } = node;

    const isExcluded = Array.isArray(excludePaths)
      ? excludePaths.some(
          (excludePath) =>
            pathname === excludePath || pathname.startsWith(`${excludePath}/`)
        )
      : false;

    let matches = false;

    if (!isExcluded) {
      if (href && (pathname === href || pathname.startsWith(`${href}/`))) {
        matches = true;
      }

      if (basePath && pathname.startsWith(basePath)) {
        matches = true;
      }
    }

    if (Array.isArray(children) && children.length > 0) {
      const childMatches = children.some(visit);
      if (childMatches) {
        matches = true;
      }
    }

    if (expandable && matches) {
      state[node.label] = true;
    }

    return matches;
  };

  (items || []).forEach(visit);

  return state;
};

const Sidebar = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const currentPath = `${location.pathname}${location.search || ""}`;

  const storedUser = useMemo(() => getStoredUser(), []);

  const navItems = useMemo(() => {
    const userRoles = Array.isArray(storedUser?.roles) ? storedUser.roles : [];
    return filterNavByRole(primaryNav, userRoles);
  }, [storedUser]);

  const derivedOpenSections = useMemo(
    () => computeOpenSections(navItems, location.pathname),
    [navItems, location.pathname]
  );

  const [openSections, setOpenSections] = useState(derivedOpenSections);

  useEffect(() => {
    // When the location changes, merge in any new auto-open sections
    setOpenSections((prev) => ({ ...prev, ...derivedOpenSections }));
  }, [derivedOpenSections]);

  const toggleSection = (label) => {
    setOpenSections((prev) => ({ ...prev, [label]: !prev[label] }));
  };

  // Close sidebar when navigating on mobile
  useEffect(() => {
    if (isOpen && window.innerWidth < 1024) {
      const handleResize = () => {
        if (window.innerWidth >= 1024) {
          // Keep sidebar open on desktop
        }
      };
      window.addEventListener("resize", handleResize);
      return () => window.removeEventListener("resize", handleResize);
    }
  }, [isOpen]);

  const handleNavClick = (href) => {
    if (href) {
      navigate(href);
      // Close sidebar on mobile after navigation
      if (window.innerWidth < 1024) {
        onClose();
      }
    }
  };

  return (
    <>
      {/* Overlay for mobile */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      <aside
        className={`fixed left-0 top-0 z-50 flex h-screen w-64 flex-col border-r border-primary/30 bg-primary text-white transition-transform duration-300 ease-in-out lg:relative lg:translate-x-0 ${
          isOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex items-center justify-between gap-3 px-6 py-5">
          <div className="flex items-center gap-3">
            <Logo className="h-auto" variant="inverse"subtitle="Operations Platform"/>
          </div>
          {/* Close button for mobile */}
          <button
            type="button"
            onClick={onClose}
            className="lg:hidden rounded-md p-1.5 text-white hover:bg-primary-dark transition"
            aria-label="Close sidebar"
          >
            <svg
              className="h-6 w-6"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
        <hr className="border-primary-dark/30" />
        <nav className="flex-1 space-y-0.5 overflow-y-auto px-6 py-1">
          {navItems.map((item) => {
            const isOpen = item.expandable ? openSections[item.label] : false;
            const activeChildHref = item.children?.some(
              (child) => child.href === location.pathname || child.href === currentPath
            );
            return (
              <div key={item.label} className="space-y-0.5">
                <button
                  type="button"
                  className={`flex w-full items-center justify-between rounded-lg px-3 py-1.5 text-sm font-medium transition ${
                    isOpen || activeChildHref
                      ? "bg-primary-dark text-white"
                      : "text-white/90 hover:bg-primary-dark/80"
                  }`}
                  onClick={() => {
                    if (item.expandable) {
                      toggleSection(item.label);
                    } else if (item.href) {
                      handleNavClick(item.href);
                    }
                  }}
                >
                  <span className="flex items-center gap-3">
                    {iconMap[item.icon]}
                    {item.label}
                  </span>
                  {item.expandable ? (
                    <svg
                      className={`h-4 w-4 transition-transform ${
                        isOpen ? "rotate-180" : ""
                      }`}
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="m6 9 6 6 6-6" />
                    </svg>
                  ) : null}
                </button>
                
                {item.children && isOpen ? (
                  <ul className="ml-9 space-y-1 text-sm text-white/80">
                    {item.children.map((child) => {
                      const isChildOpen = child.expandable
                        ? openSections[child.label]
                        : false;
                      const activeGrandChildHref = child.children?.some(
                        (grandChild) =>
                          grandChild.href === location.pathname ||
                          grandChild.href === currentPath
                      );
                      const isChildActive = child.basePath
                        ? location.pathname.startsWith(child.basePath)
                        : false;

                      return (
                        <li key={child.label} className="space-y-0.5">
                          {child.expandable ? (
                            <>
                              <button
                                type="button"
                                onClick={() => toggleSection(child.label)}
                                className={`flex w-full items-center justify-between rounded-lg px-2 py-1 text-left transition hover:bg-primary-dark/60 ${
                                  isChildOpen ||
                                  isChildActive ||
                                  activeGrandChildHref
                                    ? "bg-primary-dark/80 font-medium text-white"
                                    : ""
                                }`}
                              >
                                <span className="flex items-center gap-2">
                                  {child.icon && iconMap[child.icon] ? (
                                    <span className="[&>svg]:h-4 [&>svg]:w-4">
                                      {iconMap[child.icon]}
                                    </span>
                                  ) : null}
                                  {child.label}
                                </span>
                                <svg
                                  className={`h-3 w-3 transition-transform ${
                                    isChildOpen ? "rotate-180" : ""
                                  }`}
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                >
                                  <path d="m6 9 6 6 6-6" />
                                </svg>
                              </button>
                              {child.children && isChildOpen ? (
                                <ul className="ml-6 space-y-1 text-xs">
                                  {child.children.map((grandChild) => (
                                    <li key={grandChild.label}>
                                      <button
                                        type="button"
                                        onClick={() =>
                                          handleNavClick(grandChild.href)
                                        }
                                        className={`flex w-full items-center rounded-lg px-2 py-1 text-left transition hover:bg-primary-dark/60 ${
                                          (grandChild.href?.includes("?")
                                            ? currentPath === grandChild.href
                                            : location.pathname === grandChild.href)
                                            ? "bg-primary-dark/80 font-medium text-white"
                                            : ""
                                        }`}
                                      >
                                        {grandChild.label}
                                      </button>
                                    </li>
                                  ))}
                                </ul>
                              ) : null}
                            </>
                          ) : (
                            <button
                              type="button"
                              onClick={() => handleNavClick(child.href)}
                              className={`flex w-full items-center gap-2 rounded-lg px-2 py-1 text-left transition hover:bg-primary-dark/60 ${
                                (child.href?.includes("?")
                                  ? currentPath === child.href
                                  : location.pathname === child.href)
                                  ? "bg-primary-dark/80 font-medium text-white"
                                  : ""
                              }`}
                            >
                              {child.icon && iconMap[child.icon] ? (
                                <span className="[&>svg]:h-4 [&>svg]:w-4">
                                  {iconMap[child.icon]}
                                </span>
                              ) : null}
                              {child.label}
                            </button>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                ) : null}
              </div>
            );
          })}
        </nav>
        <hr className="border-primary-dark/30" />
        <div className="px-6 py-4 text-xs uppercase tracking-[0.3em] text-white/60">
          © {new Date().getFullYear()} Globotech
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
